import { FunctionComponent, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import styles from "./NotificationComponent.module.css";
import { useApolloClient, useMutation } from "@apollo/client";
import { CLEAR_ALL_NOTIFICATION, GET_NOTIFICATIONS, UPDATE_READ_STATUS } from "./api/NotificationQuery";
import { notificationData} from '../../../helper'
import { CircularProgress } from "@mui/material";
import { globalContext } from "../../../Provider/AppProvider";
import moment from "moment";
import "moment-timezone";
import images from "../../../assets/images";
import { useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import {
  formatCustomerType,
  formatNotesForDisplay,
  truncateNotePreview,
} from "../../../utils/listing-alert-display";
import { getNotificationQueryVariables, mergeNotificationQueryIntoGlobalState } from "../../../utils/notification-query-variables";
import { getNotificationTypeLabel } from "../../../utils/notification-feed-labels";

interface NotificationProps{
  closePopup: () => void
  preferredPeriod?: "30D" | "90D" | "12M" | "All"
  statusFilters?: {
    pending: boolean;
    confirmed: boolean;
    fulfilled: boolean;
    cancelled: boolean;
    refunded: boolean;
  }
}

const NotificationComponent: FunctionComponent<NotificationProps> = ({
  closePopup,
  preferredPeriod = "All",
  statusFilters = {
    pending: true,
    confirmed: true,
    fulfilled: true,
    cancelled: true,
    refunded: true
  }
}) => {
  const normalizePeriod = (period: string): "30D" | "90D" | "12M" | "All" => {
    if (period === "30D" || period === "90D" || period === "12M" || period === "All") {
      return period;
    }
    return "All";
  };
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { DefaultProfile } = images;
  const [showNotifications,setShowNotifications] = useState<boolean>(false);
  const [allRecentNotifications,setAllRecentNotifications] = useState<notificationData[]>([]);
  const [allPastNotifications,setAllPastNotifications] = useState<notificationData[]>([]);
  const [allRecentNotificationsSummary,setAllRecentNotificationsSummary] = useState<notificationData[]>([]);
  const [allPastNotificationsSummary,setAllPastNotificationsSummary] = useState<notificationData[]>([]);
  const [clearNotifications,setClearNotifications] = useState<boolean>(false);
  const [hiddenNotificationIds, setHiddenNotificationIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"activity" | "summary">("activity");
  const [selectedPeriod, setSelectedPeriod] = useState<"30D" | "90D" | "12M" | "All">(normalizePeriod(preferredPeriod));
  const latestFetchId = useRef<number>(0);
  const {globalState,handleGlobalState} = useContext<any>(globalContext)

  const getSinceDateByPeriod = (period: "30D" | "90D" | "12M" | "All"): string => {
    if (period === "All") {
      return "1970-01-01T00:00:00.000Z";
    }
    const baseDate = new Date();
    if (period === "12M") {
      baseDate.setMonth(baseDate.getMonth() - 12);
      return baseDate.toISOString();
    }
    const days = period === "30D" ? 30 : 90;
    baseDate.setDate(baseDate.getDate() - days);
    return baseDate.toISOString();
  };

  const fetchNotifications = async () => {
    const fetchId = ++latestFetchId.current;
    setShowNotifications(false);
    try {
      const { data } = await apolloClient.query({
        query: GET_NOTIFICATIONS,
        variables: getNotificationQueryVariables(globalState?.profile?.id),
        fetchPolicy: "network-only"
      });

      if (fetchId !== latestFetchId.current) {
        return;
      }

      const latestRecent = data?.current ?? [];
      const latestPast = data?.past ?? [];
      const latestRecentSummary = data?.currentAll ?? [];
      const latestPastSummary = data?.pastAll ?? [];
      setAllRecentNotifications(latestRecent);
      setAllPastNotifications(latestPast);
      setAllRecentNotificationsSummary(latestRecentSummary);
      setAllPastNotificationsSummary(latestPastSummary);
      setHiddenNotificationIds([]);
      handleGlobalState((prev: typeof globalState) =>
        mergeNotificationQueryIntoGlobalState(prev, data),
      );
      setShowNotifications(true);
    } catch (err) {
      console.error("Failed to load notifications", err);
      if (fetchId !== latestFetchId.current) {
        return;
      }
      setAllRecentNotifications([]);
      setAllPastNotifications([]);
      setAllRecentNotificationsSummary([]);
      setAllPastNotificationsSummary([]);
      setShowNotifications(true);
    }
  };

  // ✅ Mutation with variables and cache eviction
  const [clearAllNotificationsMutation] = useMutation(CLEAR_ALL_NOTIFICATION, {
    onCompleted: () => {
      // Clear Apollo cache fields to prevent stale data
      apolloClient.cache.evict({ fieldName: "current" });
      apolloClient.cache.evict({ fieldName: "past" });
      apolloClient.cache.evict({ fieldName: "currentAll" });
      apolloClient.cache.evict({ fieldName: "pastAll" });
      apolloClient.cache.gc(); // garbage collect
      fetchNotifications();
    },
    onError: (error) => {
      console.error("Clear all failed", error);
      fetchNotifications(); // revert optimistic update
    },
  });

  const handleClearAll = async () => {
    // 1. Optimistic UI clear
    setAllRecentNotifications([]);
    setAllPastNotifications([]);
    setAllRecentNotificationsSummary([]);
    setAllPastNotificationsSummary([]);
    setHiddenNotificationIds([]);
    
    // 2. Reset global notification count
    const tempData = { ...globalState };
    tempData.notificationCount = { recent: 0, past: 0 };
    handleGlobalState(tempData);
    
    // 3. Call mutation with profileId
    try {
      await clearAllNotificationsMutation({
        variables: {
          profileId: globalState?.profile?.id   // ✅ Required
        }
      });
    } catch {
      // Error handled in onError
    }
  };

  const [updateReadStatus] = useMutation(UPDATE_READ_STATUS, {
    fetchPolicy: "no-cache",
  });

  const handleNotificationClick = (selectedNotification: notificationData) => {
    const tempState = {...globalState};
    tempState.notification.text =
      selectedNotification?.type === "DeleteUser"
        ? `${selectedNotification?.title.slice(0, selectedNotification?.title.indexOf("account"))} [deleted user]`
        : selectedNotification?.title.slice(0, selectedNotification?.title.indexOf("requested"));
    tempState.notification.notificationClicked = true;
    tempState.notification.ticketId = selectedNotification?.ticket?.t_id ?? null;
    tempState.notification.tpId = selectedNotification?.ticket_placement?.tp_id ?? null;
    tempState.notification.tp_id_array = selectedNotification?.tp_id_array ?? null;
    tempState.notification.notificationSearchCancel = !globalState.notification.notificationSearchCancel;
    handleGlobalState(tempState);
    updateReadStatus({
      variables: {
        feedId: selectedNotification?.f_id
      }
    });
    navigate("/managetickets");
    closePopup();
  };

  const getTimeLabel = (notificationDate: string): string => {
    const now = moment.utc();
    const target = moment(notificationDate);
    const diffMinutes = now.diff(target, "minutes");
    if (diffMinutes < 60) {
      return `${diffMinutes} mins ago`;
    }
    const diffHours = now.diff(target, "hours");
    if (diffHours < 24) {
      return `${diffHours} hours ago`;
    }
    const diffDays = now.startOf("day").diff(target.startOf("day"), "days");
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  };

  useEffect(()=>{
    const tempValue = {...globalState}
    if(globalState.error.errorClicked){
      tempValue.error.errorClicked = false;
      tempValue.globalSearch.searchtext = "";
      tempValue.error.text = "";
      tempValue.error.approximateTpArray = null
      tempValue.error.ticketPlacementId = null
      tempValue.error.status = ""
      handleGlobalState(tempValue)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])
  
  useEffect(()=>{
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[clearNotifications])

  useEffect(() => {
    setSelectedPeriod(normalizePeriod(preferredPeriod));
  }, [preferredPeriod]);

  const sinceDateMs = new Date(getSinceDateByPeriod(selectedPeriod)).getTime();
  const getListingAlertDetails = (feed: notificationData) => {
    if (feed?.type !== "Added") {
      return null;
    }
    const ticketUser = feed?.ticket?.user;
    const notes = (ticketUser?.user_notes ?? []).map((note) => ({
      n_type: note?.noteType,
      n_content: note?.noteContent,
    }));
    const internalNotes = formatNotesForDisplay(notes, "internal");
    const externalNotes = formatNotesForDisplay(notes, "external");
    return {
      customerType: formatCustomerType(ticketUser?.pricingMode),
      internalNotes,
      externalNotes,
      hasInternalNotes: internalNotes !== "N/A",
      hasExternalNotes: externalNotes !== "N/A",
    };
  };

  const getStatusBucket = (feedType?: string): keyof typeof statusFilters | null => {
    const normalized = String(feedType || "").toLowerCase();
    if (normalized === "added" || normalized === "delistinprogress" || normalized === "pending") return "pending";
    if (normalized === "list" || normalized === "confirmed") return "confirmed";
    if (normalized === "sold" || normalized === "fulfilled") return "fulfilled";
    if (normalized === "delist" || normalized === "deleteuser" || normalized === "cancelled" || normalized === "canceled") return "cancelled";
    if (normalized === "refunded") return "refunded";
    return null;
  };

  const filterByStatus = (notifications: notificationData[] = []) =>
    notifications.filter((item) => {
      const bucket = getStatusBucket(item.type);
      if (!bucket) return true;
      return statusFilters[bucket];
    });

  const filterByPeriod = (notifications: notificationData[] = []) =>
    notifications.filter((item) => {
      const itemDate = new Date(item.notification).getTime();
      return Number.isFinite(itemDate) && itemDate >= sinceDateMs;
    });

  const dedupeNotificationsByFeedId = (notifications: notificationData[] = []) => {
    const seen = new Set<string>();
    return notifications.filter((item) => {
      if (!item?.f_id || seen.has(item.f_id)) {
        return false;
      }
      seen.add(item.f_id);
      return true;
    });
  };

  const recentNotifications = dedupeNotificationsByFeedId(filterByPeriod(filterByStatus(allRecentNotifications)));
  const pastNotifications = dedupeNotificationsByFeedId(filterByPeriod(filterByStatus(allPastNotifications)));
  const recentNotificationsCount = recentNotifications.length;
  const pastNotificationsCount = pastNotifications.length;
  const summaryRecentNotificationsCount = filterByPeriod(filterByStatus(allRecentNotificationsSummary)).length;
  const summaryPastNotificationsCount = filterByPeriod(filterByStatus(allPastNotificationsSummary)).length;

  const visibleRecentNotifications = recentNotifications.filter((item) => !hiddenNotificationIds.includes(item.f_id));
  const visiblePastNotifications = pastNotifications.filter((item) => !hiddenNotificationIds.includes(item.f_id));
  const visibleRecentNotificationsCount = visibleRecentNotifications.length;
  const visiblePastNotificationsCount = visiblePastNotifications.length;
  const allNotifications = [...visibleRecentNotifications, ...visiblePastNotifications].sort(
    (a: notificationData, b: notificationData) =>
      new Date(b.notification).getTime() - new Date(a.notification).getTime()
  );

  const handleHideNotification = async (event: MouseEvent<HTMLButtonElement>, feedId: string) => {
    event.stopPropagation();
    setHiddenNotificationIds((prev) => [...prev, feedId]);

    const isRecent = recentNotifications.some((item) => item.f_id === feedId);
    const isPast = pastNotifications.some((item) => item.f_id === feedId);
    const tempData = { ...globalState };
    tempData.notificationCount = {
      recent: isRecent ? Math.max(globalState.notificationCount.recent - 1, 0) : globalState.notificationCount.recent,
      past: isPast ? Math.max(globalState.notificationCount.past - 1, 0) : globalState.notificationCount.past,
    };
    handleGlobalState(tempData);

    try {
      await updateReadStatus({
        variables: {
          feedId,
        },
      });
    } catch {
      // ignore failures for UI dismissal
    }
  };

  return (
    <div className={styles.notification}>
      <div className={styles.topTabs}>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "activity" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("activity")}
        >
          Activity
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "summary" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        {showNotifications && (visibleRecentNotificationsCount + visiblePastNotificationsCount) > 0 && (
          <button type="button" className={styles.clearAllBtn} onClick={handleClearAll}>
            Clear all
          </button>
        )}
      </div>

      <div className={styles.contentArea}>
        {!showNotifications && <div className={styles.loader}><CircularProgress color="inherit" /></div>}

        {showNotifications && activeTab === "activity" && (
          <div className={styles.notificationList}>
            {allNotifications.length === 0 ? (
              <p className={styles.emptyState}>No notifications yet</p>
            ) : (
              allNotifications.map((feed: notificationData) => {
                const listingDetails = getListingAlertDetails(feed);
                return (
                <div
                  role="button"
                  tabIndex={0}
                  key={feed.f_id}
                  className={styles.notificationItem}
                  onClick={() => handleNotificationClick(feed)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleNotificationClick(feed);
                    }
                  }}
                >
                  <img
                    className={styles.avatar}
                    alt="event"
                    src={feed?.ticket?.event?.eventImage ? feed?.ticket?.event?.eventImage : DefaultProfile}
                  />
                  <div className={styles.messageContent}>
                    <p className={styles.typeBadge}>{getNotificationTypeLabel(feed?.type)}</p>
                    <p className={`${styles.title} ${feed?.type === "DeleteUser" ? styles.deleteTitle : ""}`}>{feed?.title ?? ""}</p>
                    <p className={styles.subTitle}>
                      {`${feed?.ticket?.event?.eventName ?? ""} ${
                        feed?.ticket?.event?.eventDate
                          ? moment(feed?.ticket?.event?.eventDate).tz("America/Chicago").format("ddd DD MMM, hh:mm A z")
                          : ""
                      }`}
                    </p>
                    <p className={styles.subTitle}>{feed?.ticket?.event?.eventAddress ?? ""}</p>
                    {listingDetails && (
                      <>
                        <p className={styles.metaLine}>
                          <span className={styles.metaLabel}>Customer type:</span> {listingDetails.customerType}
                        </p>
                        <p className={`${styles.metaLine} ${listingDetails.hasInternalNotes ? styles.noteHighlight : ""}`}>
                          <span className={styles.metaLabel}>Internal notes:</span>{" "}
                          {truncateNotePreview(listingDetails.internalNotes)}
                        </p>
                        <p className={`${styles.metaLine} ${listingDetails.hasExternalNotes ? styles.noteHighlight : ""}`}>
                          <span className={styles.metaLabel}>External notes:</span>{" "}
                          {truncateNotePreview(listingDetails.externalNotes)}
                        </p>
                      </>
                    )}
                  </div>
                <button
                  type="button"
                  className={styles.closeNotificationBtn}
                  aria-label="Dismiss notification"
                  onClick={(event) => handleHideNotification(event, feed.f_id)}
                >
                  <IoClose size={16} />
                </button>
                  <span className={styles.time}>{getTimeLabel(feed.notification)}</span>
                </div>
              )})
            )}
          </div>
        )}

        {showNotifications && activeTab === "summary" && (
          <div className={styles.summaryBlock}>
            <div className={styles.summaryRow}>
              <span>Total Notifications</span>
              <strong>{summaryRecentNotificationsCount + summaryPastNotificationsCount}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Recent</span>
              <strong>{summaryRecentNotificationsCount}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Past</span>
              <strong>{summaryPastNotificationsCount}</strong>
            </div>
          </div>
        )}
      </div>

      <div className={styles.periodButtons}>
        <button type="button" className={`${styles.periodBtn} ${selectedPeriod === "30D" ? styles.activePeriod : ""}`} onClick={() => setSelectedPeriod("30D")}>30D</button>
        <button type="button" className={`${styles.periodBtn} ${selectedPeriod === "90D" ? styles.activePeriod : ""}`} onClick={() => setSelectedPeriod("90D")}>90D</button>
        <button type="button" className={`${styles.periodBtn} ${selectedPeriod === "12M" ? styles.activePeriod : ""}`} onClick={() => setSelectedPeriod("12M")}>12M</button>
        <button type="button" className={`${styles.periodBtn} ${selectedPeriod === "All" ? styles.activePeriod : ""}`} onClick={() => setSelectedPeriod("All")}>All</button>
      </div>
    </div>
  );
};

export default NotificationComponent;